import { Link, useNavigate, useSearchParams } from "react-router-dom";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./Navbar.css";
import { useUser } from "../contexts/UserContext";

export default function Navbar() {
  const navbarRef = useRef<HTMLElement | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const mobileAutoCloseTimerRef = useRef<number | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [mobileSearchOpen, setMobileSearchOpen] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 640,
  );

  const { totalShoppingQuantity, refreshShoppingCount } = useUser();

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 640 : false,
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Sync local state with URL params
  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== searchTerm) setSearchTerm(q || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Refresh shopping count
  useEffect(() => {
    refreshShoppingCount();
  }, [refreshShoppingCount]);

  // Set --navbar-height on :root so ALL pages can use it
  useLayoutEffect(() => {
    const el = navbarRef.current;
    if (!el) return;

    const setVar = () => {
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${el.offsetHeight}px`,
      );
    };

    setVar();
    window.addEventListener("resize", setVar);

    let ro: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(() => setVar());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("resize", setVar);
      if (ro) ro.disconnect();
    };
  }, []);

  // Focus mobile input when opened
  useEffect(() => {
    if (!mobileSearchOpen) return;
    setTimeout(() => mobileSearchInputRef.current?.focus(), 0);
  }, [mobileSearchOpen]);

  useEffect(() => {
    return () => clearMobileAutoCloseTimer();
  }, []);

  const submitSearch = useCallback(() => {
    const term = searchTerm.trim().toLowerCase();
    mobileSearchInputRef.current?.blur();
    setMobileSearchOpen(false);
    navigate(term ? `/search/${encodeURIComponent(term)}` : "/");
    setSearchTerm("");
    setMobileSearchOpen(false);
    clearMobileAutoCloseTimer();
  }, [navigate, searchTerm]);

  const clearMobileAutoCloseTimer = () => {
    if (mobileAutoCloseTimerRef.current) {
      window.clearTimeout(mobileAutoCloseTimerRef.current);
      mobileAutoCloseTimerRef.current = null;
    }
  };

  const startMobileAutoCloseTimer = () => {
    clearMobileAutoCloseTimer();
    mobileAutoCloseTimerRef.current = window.setTimeout(() => {
      // Close only if user didn't focus the input
      if (document.activeElement !== mobileSearchInputRef.current) {
        setMobileSearchOpen(true);
      }
    }, 3000);
  };
  const onSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") submitSearch();
    },
    [submitSearch],
  );

  return (
    <header ref={navbarRef as any} className="navbar">
      <div className="navbar-container">
        {/* LEFT */}
        <div className="navbar-left">
          <Link to="/" title="🏠" className="logo-link">
            <div className="logo-text">
              <div>
                ВСИЧКИ
                <span style={{ color: "#FFF6E7" }}> %</span>
              </div>
              <div>БРОШУРИ</div>
            </div>
          </Link>
        </div>

        {/* CENTER (desktop search only) */}
        <div className="navbar-center">
          <div className="search-container desktop-only" data-tutorial="search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Търси продукти: мляко, масло, яйца..."
              className="search-input"
            />
            <button
              className="search-icon-btn"
              onClick={submitSearch}
              type="button"
              aria-label="Търси"
              title="Търси"
            >
              <img
                src="/assets/img/search.png"
                alt=""
                className="action-icon-search"
              />
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="navbar-right">
          <div className="user-actions" data-tutorial={!isMobile ? "account" : undefined}>
            {/* Desktop-only Favorites */}
            {!isMobile && (
              <Link to="/favorite" className="action-btn" title="Любими">
                <img
                  src="/assets/img/favorite.png"
                  alt="Любими"
                  className="action-icon"
                />
              </Link>
            )}

            {/* Mobile search next to Profile */}
            {!mobileSearchOpen ? (
              <button
                type="button"
                className="action-btn mobile-only"
                data-tutorial="search"
                title="Търси"
                aria-label="Отвори търсене"
                onClick={() => {
                  setMobileSearchOpen(true);
                  startMobileAutoCloseTimer();
                }}
              >
                <img
                  src="/assets/img/search.png"
                  alt=""
                  className="action-icon"
                />
              </button>
            ) : (
              <div className="mobile-search-expanded mobile-only" data-tutorial="search">
                <input
                  ref={mobileSearchInputRef}
                  className="mobile-search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  onFocus={() => clearMobileAutoCloseTimer()}
                  onBlur={() => setMobileSearchOpen(true)}
                  placeholder="Търси: мляко, масло, яйца..."
                />

                <button
                  type="button"
                  className="mobile-search-submit-btn"
                  onMouseDown={(e) => e.preventDefault()} // keep focus so click works
                  onClick={submitSearch}
                  aria-label="Търси"
                  title="Търси"
                >
                  <img
                    src="/assets/img/search.png"
                    alt=""
                    className="action-icon-search"
                  />
                </button>
              </div>
            )}

            {/* Profile */}
            {!isMobile && (
              <Link to="/profile" className="action-btn" title="Профил">
                <img
                  src="/assets/img/profile.png"
                  alt="Профил"
                  className="action-icon"
                />
              </Link>
            )}

            {/* Shopping */}
            {!isMobile && (
              <Link
                to="/shoppinglist"
                className="action-btn shopping-list-btn"
                title="Списък за пазаруване"
              >
                <img
                  src="/assets/img/shopping-list.png"
                  alt="Списък за пазаруване"
                  className="action-icon"
                />
                {totalShoppingQuantity > 0 && (
                  <span className="notification-badge">
                    {totalShoppingQuantity > 99 ? "99+" : totalShoppingQuantity}
                  </span>
                )}
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
